import React, { useState } from "react";

export default function HowItWorks({
  depositBonusPer = 30,
  maxBonus = 2100,
  lotsPer100 = 2.5,
}) {
  const [activeStep, setActiveStep] = useState(1);

  const steps = [
    {
      step: 1,
      tag: "STEP 1 OF 3",
      title: "Deposit Funds",
      navLabel: "Deposit Funds",
      icon: "bx bx-wallet",
      description: `Choose your wallet and deposit funds. Your bonus is calculated as ${depositBonusPer}% of your deposit.`,
      note: `The maximum bonus you can receive is $${Number(maxBonus).toLocaleString()}. Select your preferred wallet and the amount — the bonus is calculated instantly.`,
    },
    {
      step: 2,
      tag: "STEP 2 OF 3",
      title: "Trade the Volume",
      navLabel: "Trade the Volume",
      icon: "bx bx-trending-up",
      description:
        "Trade on MT5 across Forex pairs, Metals, Indices, or Crypto to complete the required lot turnover.",
      note: `Every $100 deposited requires ${lotsPer100} standard lots. All completed positions are automatically tracked and updated in real time.`,
    },
    {
      step: 3,
      tag: "STEP 3 OF 3",
      title: "Withdraw Everything",
      navLabel: "Withdraw Everything",
      icon: "bx bx-up-arrow-circle",
      description: `Once required lots are fulfilled, your ${depositBonusPer}% bonus converts directly to real balance with zero restrictions.`,
      note: "Withdraw your original deposit, all accumulated trading profits, and the full unlocked bonus amount at any time without fees.",
    },
  ];

  const currentStepData = steps.find((s) => s.step === activeStep) || steps[0];

  return (
    <div className="my-5">
      <div className="mb-4">
        <h5
          className="fw-bold mb-1"
          style={{ color: "var(--bs-emphasis-color)", fontSize: "1.2rem" }}
        >
          How It Works
        </h5>
        <p className="mb-0 font-size-13" style={{ color: "var(--bs-secondary-color)" }}>
          Follow these three simple steps
        </p>
      </div>

      <div className="d-flex justify-content-center align-items-center mb-4 px-2">
        <div
          className="position-relative w-100"
          style={{ maxWidth: "560px" }}
        >
          {/* Connecting Line Track & Active Progress */}
          <div
            className="position-absolute"
            style={{
              top: "25px",
              left: "16.66%",
              right: "16.66%",
              height: "1px",
              transform: "translateY(-50%)",
              zIndex: 1,
            }}
          >
            {/* Base Visible Line */}
            <div
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: "var(--bs-border-color-translucent)",
                borderRadius: "3px",
              }}
            />

            {/* Active Progress Line */}
            <div
              className="position-absolute top-0 start-0 h-100"
              style={{
                width:
                  activeStep === 1
                    ? "0%"
                    : activeStep === 2
                      ? "50%"
                      : "100%",
                backgroundColor: "var(--bs-primary)",
                borderRadius: "3px",
                transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
            />
          </div>

          {/* Stepper Circles & Labels */}
          <div
            className="d-flex align-items-start position-relative"
            style={{ zIndex: 2 }}
          >
            {steps.map((item) => {
              const isActive = activeStep === item.step;
              const isCompleted = activeStep > item.step;

              return (
                <div
                  key={item.step}
                  className="d-flex flex-column align-items-center text-center cursor-pointer"
                  style={{ flex: 1, cursor: "pointer" }}
                  onClick={() => setActiveStep(item.step)}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: "50px",
                      height: "50px",
                      backgroundColor:
                        isActive || isCompleted
                          ? "var(--bs-primary)"
                          : "var(--bs-secondary-bg)",
                      color:
                        isActive || isCompleted
                          ? "#fff"
                          : "var(--bs-secondary-color)",
                      boxShadow: isActive
                        ? "0 4px 14px rgba(var(--bs-primary-rgb), 0.35)"
                        : "0 2px 6px rgba(0, 0, 0, 0.05)",
                      border:
                        isActive || isCompleted
                          ? "1px solid var(--bs-primary)"
                          : "1px solid var(--bs-border-color-translucent)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    <i
                      className={`${isCompleted ? "bx bx-check" : item.icon
                        } font-size-22`}
                    ></i>
                  </div>
                  <span
                    className="mt-2 font-size-12 fw-semibold px-1"
                    style={{
                      color: isActive
                        ? "var(--bs-primary)"
                        : "var(--bs-secondary-color)",
                      transition: "color 0.2s ease",
                    }}
                  >
                    {item.navLabel}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div
        className="card border shadow-sm mb-3"
        style={{
          borderRadius: "var(--bs-border-radius-xl)",
          backgroundColor: "var(--bs-card-bg)",
          borderColor: "var(--bs-border-color)",
        }}
      >
        <div className="card-body p-4">
          <div className="d-flex align-items-start gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
              style={{
                width: "52px",
                height: "52px",
                backgroundColor: "var(--bs-primary-bg-subtle)",
                color: "var(--bs-primary)",
                border: "1px solid var(--bs-primary-border-subtle)",
              }}
            >
              <i className={`${currentStepData.icon} font-size-24`}></i>
            </div>

            <div className="flex-grow-1">
              <span
                className="badge mb-2 px-2 py-1 text-uppercase"
                style={{
                  backgroundColor: "var(--bs-primary-bg-subtle)",
                  color: "var(--bs-primary)",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.5px",
                  borderRadius: "var(--bs-border-radius-sm)",
                }}
              >
                {currentStepData.tag}
              </span>
              <h5
                className="fw-bold mb-2"
                style={{ color: "var(--bs-emphasis-color)", fontSize: "1.1rem" }}
              >
                {currentStepData.title}
              </h5>
              <p
                className="mb-3 font-size-13"
                style={{ color: "var(--bs-secondary-color)", lineHeight: "1.6" }}
              >
                {currentStepData.description}
              </p>

              <div
                className="p-3 rounded-3 d-flex align-items-center gap-2 font-size-12"
                style={{
                  backgroundColor: "var(--bs-tertiary-bg)",
                  border: "1px solid var(--bs-border-color)",
                  color: "var(--bs-emphasis-color)",
                }}
              >
                <i
                  className="bx bx-info-circle font-size-16 flex-shrink-0"
                  style={{ color: "var(--bs-primary)" }}
                ></i>
                <span>{currentStepData.note}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
        {steps.map((s) => (
          <button
            key={s.step}
            type="button"
            className="border-0 p-0"
            onClick={() => setActiveStep(s.step)}
            style={{
              width: activeStep === s.step ? "22px" : "8px",
              height: "8px",
              borderRadius: "4px",
              backgroundColor: activeStep === s.step
                ? "var(--bs-primary)"
                : "var(--bs-border-color-translucent)",
              transition: "all 0.25s ease",
              cursor: "pointer",
            }}
            title={`Go to step ${s.step}`}
          />
        ))}
      </div>
    </div>
  );
}
